// | Ordered multiset is a multiset that retains insertion order.
// |
// | Implemented using association list, so has linear lookup and modification
// | of a single element.
// |
// | `fromOccurArray` is O(n^2).
import * as $runtime from "../runtime.js";
import * as Data$dArray from "../Data.Array/index.js";
import * as Data$dEq from "../Data.Eq/index.js";
import * as Data$dFoldable from "../Data.Foldable/index.js";
import * as Data$dFunctor from "../Data.Functor/index.js";
import * as Data$dMap$dInternal from "../Data.Map.Internal/index.js";
import * as Data$dMaybe from "../Data.Maybe/index.js";
import * as Data$dMultiSet from "../Data.MultiSet/index.js";
import * as Data$dOrd from "../Data.Ord/index.js";
import * as Data$dOrdering from "../Data.Ordering/index.js";
import * as Data$dSemigroup from "../Data.Semigroup/index.js";
import * as Data$dSemiring from "../Data.Semiring/index.js";
import * as Data$dShow from "../Data.Show/index.js";
import * as Data$dTraversable from "../Data.Traversable/index.js";
import * as Data$dTuple from "../Data.Tuple/index.js";
const $InsertPosition = tag => ({tag});
const traverse = /* #__PURE__ */ (() => Data$dTraversable.traversableTuple.traverse(Data$dMaybe.applicativeMaybe))();
const First = /* #__PURE__ */ $InsertPosition("First");
const Last = /* #__PURE__ */ $InsertPosition("Last");
const unOMS = v => v;
const toOccurArray = unOMS;
const toMultiSet = dictOrd => Data$dMultiSet.fromOccurArray(dictOrd);
const size = /* #__PURE__ */ (() => {
  const $0 = Data$dFoldable.foldlArray(Data$dSemiring.intAdd)(0);
  const $1 = Data$dFunctor.arrayMap(Data$dTuple.snd);
  return x => $0($1(x));
})();
const showOrderedMultiSet = dictShow => {
  const show = Data$dShow.showArrayImpl(v => "(Tuple " + dictShow.show(v._1) + " " + Data$dShow.showIntImpl(v._2) + ")");
  return {show: x => "(fromOccurArray " + show(x) + ")"};
};
const ordOrderedMultiSet = dictOrd => Data$dOrd.ordArray(Data$dTuple.ordTuple(dictOrd)(Data$dOrd.ordInt));
const occur = dictEq => x => {
  const $0 = Data$dFoldable.find(Data$dFoldable.foldableArray)(v => dictEq.eq(v._1)(x));
  return x$1 => {
    const $1 = $0(x$1);
    if ($1.tag === "Nothing") { return 0; }
    if ($1.tag === "Just") { return $1._1._2; }
    $runtime.fail();
  };
};
const numDistinctElems = x => x.length;
const $$null = x => x.length === 0;
const findIndex = dictEq => needle => v => Data$dArray.findIndexImpl(Data$dMaybe.Just, Data$dMaybe.Nothing, v1 => dictEq.eq(v1._1)(needle), v);
const eqOrderedMultiSet = dictEq => ({eq: Data$dEq.eqArrayImpl(x => y => dictEq.eq(x._1)(y._1) && x._2 === y._2)});
const empty = [];
const distinctElems = /* #__PURE__ */ Data$dFunctor.arrayMap(Data$dTuple.fst);
const toSet = dictOrd => {
  const $0 = Data$dFoldable.foldlArray(m => a => Data$dMap$dInternal.insert(dictOrd)(a)()(m))(Data$dMap$dInternal.Leaf);
  return x => $0(distinctElems(x));
};
const alterCount = dictEq => f => insertPos => x => v => {
  const $0 = Data$dArray.findIndexImpl(Data$dMaybe.Just, Data$dMaybe.Nothing, v1 => dictEq.eq(v1._1)(x), v);
  const alterExisting = (() => {
    if ($0.tag === "Just") {
      return Data$dArray.alterAt($0._1)(traverse(x$1 => {
        const $1 = f(x$1);
        if ($1 <= 0) { return Data$dMaybe.Nothing; }
        return Data$dMaybe.$Maybe("Just", $1);
      }))(v);
    }
    if ($0.tag === "Nothing") { return Data$dMaybe.Nothing; }
    $runtime.fail();
  })();
  if (alterExisting.tag === "Just") { return alterExisting._1; }
  if (alterExisting.tag === "Nothing") {
    const count = f(0);
    if (count > 0) {
      if (insertPos.tag === "First") { return Data$dSemigroup.concatArray([Data$dTuple.$Tuple(x, count)])(v); }
      if (insertPos.tag === "Last") { return Data$dArray.snoc(v)(Data$dTuple.$Tuple(x, count)); }
      $runtime.fail();
    }
    return v;
  }
  $runtime.fail();
};
const $$delete = dictEq => alterCount(dictEq)(v => 0)(Last);
const alterValueMatching = dictEq => pred => $$new => s => {
  const newCount = pred($$new) ? 0 : occur(dictEq)($$new)(s);
  return Data$dFunctor.arrayMap(v1 => {
    if (pred(v1._1)) { return Data$dTuple.$Tuple($$new, v1._2 + newCount | 0); }
    return v1;
  })(pred($$new) ? s : alterCount(dictEq)(v => 0)(Last)($$new)(s));
};
const insertMany = dictEq => pos => x => delta => alterCount(dictEq)(v => v + delta | 0)(pos)(x);
const fromOccurArray = dictEq => Data$dFoldable.foldlArray(acc => v => {
  const $0 = v._2;
  return alterCount(dictEq)(v$1 => v$1 + $0 | 0)(Last)(v._1)(acc);
})([]);
const restoreOrder = dictEq => {
  const fromOccurArray1 = fromOccurArray(dictEq);
  return defaultOrder => {
    const $0 = Data$dArray.sortBy((() => {
      const $0 = v => {
        const $0 = v._1;
        return Data$dArray.findIndexImpl(Data$dMaybe.Just, Data$dMaybe.Nothing, v1 => dictEq.eq(v1._1)($0), defaultOrder);
      };
      return x => y => {
        const $1 = $0(x);
        const $2 = $0(y);
        if ($1.tag === "Just") {
          if ($2.tag === "Just") { return Data$dOrd.ordInt.compare($1._1)($2._1); }
          if ($2.tag === "Nothing") { return Data$dOrdering.LT; }
          $runtime.fail();
        }
        if ($1.tag === "Nothing") {
          if ($2.tag === "Nothing") { return Data$dOrdering.EQ; }
          return Data$dOrdering.GT;
        }
        $runtime.fail();
      };
    })());
    return x => fromOccurArray1($0(Data$dMultiSet.toOccurArray(x)));
  };
};
const insert = dictEq => pos => x => insertMany(dictEq)(pos)(x)(1);
const fromArray = dictOrd => {
  const insert1 = insert(dictOrd.Eq0());
  return Data$dFoldable.foldlArray(acc => x => insert1(Last)(x)(acc))([]);
};
const semigroupOMS = dictEq => (
  {
    append: xs => v => Data$dFoldable.foldlArray(acc => v1 => {
      const $0 = v1._2;
      return alterCount(dictEq)(v$1 => v$1 + $0 | 0)(Last)(v1._1)(acc);
    })(xs)(v)
  }
);
const monoidOMS = dictEq => {
  const semigroupOMS1 = semigroupOMS(dictEq);
  return {mempty: [], Semigroup0: () => semigroupOMS1};
};
export {
  $InsertPosition,
  First,
  Last,
  alterCount,
  alterValueMatching,
  $$delete as delete,
  distinctElems,
  empty,
  eqOrderedMultiSet,
  findIndex,
  fromArray,
  fromOccurArray,
  insert,
  insertMany,
  monoidOMS,
  $$null as null,
  numDistinctElems,
  occur,
  ordOrderedMultiSet,
  restoreOrder,
  semigroupOMS,
  showOrderedMultiSet,
  size,
  toMultiSet,
  toOccurArray,
  toSet,
  traverse,
  unOMS
};
